// Medication → underwriting condition lookup.
//
// Powers the medication search on the FEX Carrier Placement tool —
// type "eliquis" and the chip pool surfaces the AFib / Blood clot
// conditions that medication points to.
//
// The KEYS in this map are lowercase generic and brand names. The
// VALUES are an array of condition name fragments that get matched
// against FEX_CONDITIONS.name (case-insensitive substring). Multiple
// medications can map to the same condition; multiple conditions can
// be implied by one medication.
//
// Not exhaustive — covers the top ~150 medications agents see at
// kitchen tables. Expand as patterns emerge.

export const MEDICATION_TO_CONDITIONS = {
  // ── Diabetes ────────────────────────────────────────────────────
  metformin:        ['Diabetes'],
  glipizide:        ['Diabetes'],
  glyburide:        ['Diabetes'],
  glimepiride:      ['Diabetes'],
  januvia:          ['Diabetes'],
  sitagliptin:      ['Diabetes'],
  jardiance:        ['Diabetes'],
  empagliflozin:    ['Diabetes'],
  ozempic:          ['Diabetes'],
  semaglutide:      ['Diabetes'],
  trulicity:        ['Diabetes'],
  victoza:          ['Diabetes'],
  mounjaro:         ['Diabetes'],
  tirzepatide:      ['Diabetes'],
  rybelsus:         ['Diabetes'],
  pioglitazone:     ['Diabetes'],
  actos:            ['Diabetes'],
  invokana:         ['Diabetes'],
  canagliflozin:    ['Diabetes'],
  insulin:          ['Diabetes'],
  lantus:           ['Diabetes'],
  humalog:          ['Diabetes'],
  novolog:          ['Diabetes'],
  basaglar:         ['Diabetes'],
  tresiba:          ['Diabetes'],

  // ── Blood thinners / AFib / Blood clots / Stent ─────────────────
  eliquis:          ['AFib', 'Blood Clots'],
  apixaban:         ['AFib', 'Blood Clots'],
  xarelto:          ['AFib', 'Blood Clots'],
  rivaroxaban:      ['AFib', 'Blood Clots'],
  warfarin:         ['AFib', 'Blood Clots'],
  coumadin:         ['AFib', 'Blood Clots'],
  pradaxa:          ['AFib', 'Blood Clots'],
  dabigatran:       ['AFib', 'Blood Clots'],
  plavix:           ['Stent', 'Heart Attack', 'Heart Surgery'],
  clopidogrel:      ['Stent', 'Heart Attack', 'Heart Surgery'],
  brilinta:         ['Stent', 'Heart Attack'],
  ticagrelor:       ['Stent', 'Heart Attack'],
  effient:          ['Stent', 'Heart Attack'],

  // ── Heart / Hypertension / CHF ──────────────────────────────────
  lisinopril:       [],   // bp alone isn't a UW issue, skip the chip
  losartan:         [],
  amlodipine:       [],
  metoprolol:       ['AFib'],
  carvedilol:       ['Congestive Heart Failure'],
  furosemide:       ['Congestive Heart Failure'],
  lasix:            ['Congestive Heart Failure'],
  spironolactone:   ['Congestive Heart Failure'],
  digoxin:          ['AFib', 'Congestive Heart Failure'],
  amiodarone:       ['AFib'],
  nitroglycerin:    ['Angina', 'Coronary Artery'],
  isosorbide:       ['Angina', 'Coronary Artery'],
  ranolazine:       ['Angina'],
  ranexa:           ['Angina'],
  entresto:         ['Congestive Heart Failure'],
  sacubitril:       ['Congestive Heart Failure'],
  jardiance:        ['Diabetes', 'Congestive Heart Failure'],

  // ── COPD / Emphysema / Asthma / Pulmonary ───────────────────────
  spiriva:          ['COPD', 'Emphysema'],
  tiotropium:       ['COPD', 'Emphysema'],
  symbicort:        ['COPD', 'Asthma'],
  advair:           ['COPD', 'Asthma'],
  trelegy:          ['COPD'],
  breo:             ['COPD', 'Asthma'],
  anoro:            ['COPD'],
  prednisone:       ['COPD', 'Rheumatoid', 'Asthma'],
  albuterol:        ['Asthma', 'COPD'],
  ventolin:         ['Asthma', 'COPD'],
  proair:           ['Asthma', 'COPD'],
  pulmicort:        ['Asthma'],
  flovent:          ['Asthma'],
  pirfenidone:      ['Pulmonary Fibrosis'],
  esbriet:          ['Pulmonary Fibrosis'],
  ofev:             ['Pulmonary Fibrosis'],
  nintedanib:       ['Pulmonary Fibrosis'],

  // ── Seizures / Epilepsy / Neurology ─────────────────────────────
  keppra:           ['Seizures', 'Epilepsy'],
  levetiracetam:    ['Seizures', 'Epilepsy'],
  lamictal:         ['Seizures', 'Epilepsy', 'Bipolar'],
  lamotrigine:      ['Seizures', 'Epilepsy', 'Bipolar'],
  dilantin:         ['Seizures', 'Epilepsy'],
  phenytoin:        ['Seizures', 'Epilepsy'],
  topamax:          ['Seizures', 'Epilepsy'],
  topiramate:       ['Seizures', 'Epilepsy'],
  depakote:         ['Seizures', 'Epilepsy', 'Bipolar'],
  valproate:        ['Seizures', 'Epilepsy', 'Bipolar'],
  gabapentin:       ['Neuropathy', 'Seizures'],
  neurontin:        ['Neuropathy', 'Seizures'],
  pregabalin:       ['Neuropathy'],
  lyrica:           ['Neuropathy'],

  // ── Parkinson's / Alzheimer's / Dementia ────────────────────────
  carbidopa:        ["Parkinson's"],
  levodopa:         ["Parkinson's"],
  sinemet:          ["Parkinson's"],
  donepezil:        ['Alzheimer', 'Dementia'],
  aricept:          ['Alzheimer', 'Dementia'],
  memantine:        ['Alzheimer', 'Dementia'],
  namenda:          ['Alzheimer', 'Dementia'],
  rivastigmine:     ['Alzheimer', 'Dementia'],
  exelon:           ['Alzheimer', 'Dementia'],

  // ── MS / Lupus / Autoimmune ─────────────────────────────────────
  rituximab:        ['Multiple Sclerosis', 'Rheumatoid', 'Lupus'],
  rituxan:          ['Multiple Sclerosis', 'Rheumatoid', 'Lupus'],
  ocrevus:          ['Multiple Sclerosis'],
  tysabri:          ['Multiple Sclerosis'],
  copaxone:         ['Multiple Sclerosis'],
  tecfidera:        ['Multiple Sclerosis'],
  humira:           ['Rheumatoid', "Crohn's", 'Ulcerative Colitis'],
  enbrel:           ['Rheumatoid'],
  methotrexate:     ['Rheumatoid', 'Lupus'],
  hydroxychloroquine: ['Lupus', 'Rheumatoid'],
  plaquenil:        ['Lupus', 'Rheumatoid'],

  // ── Mental health ──────────────────────────────────────────────
  lithium:          ['Bipolar'],
  abilify:          ['Bipolar', 'Schizophrenia', 'Depression'],
  aripiprazole:     ['Bipolar', 'Schizophrenia'],
  risperdal:        ['Schizophrenia', 'Bipolar'],
  risperidone:      ['Schizophrenia', 'Bipolar'],
  clozaril:         ['Schizophrenia'],
  clozapine:        ['Schizophrenia'],
  olanzapine:       ['Schizophrenia', 'Bipolar'],
  zyprexa:          ['Schizophrenia', 'Bipolar'],
  seroquel:         ['Schizophrenia', 'Bipolar'],
  quetiapine:       ['Schizophrenia', 'Bipolar'],
  sertraline:       ['Depression', 'PTSD'],
  zoloft:           ['Depression', 'PTSD'],
  fluoxetine:       ['Depression'],
  prozac:           ['Depression'],
  citalopram:       ['Depression'],
  celexa:           ['Depression'],
  escitalopram:     ['Depression'],
  lexapro:          ['Depression'],
  paroxetine:       ['Depression'],
  paxil:            ['Depression'],
  venlafaxine:      ['Depression'],
  effexor:          ['Depression'],
  duloxetine:       ['Depression', 'Neuropathy'],
  cymbalta:         ['Depression', 'Neuropathy'],
  trazodone:        ['Depression'],
  buspirone:        ['Depression'],
  bupropion:        ['Depression'],
  wellbutrin:       ['Depression'],

  // ── Kidney / Dialysis ───────────────────────────────────────────
  epogen:           ['Dialysis', 'Kidney'],
  procrit:          ['Dialysis', 'Kidney'],
  sevelamer:        ['Dialysis', 'Kidney'],
  renvela:          ['Dialysis', 'Kidney'],

  // ── Liver / Cirrhosis / Hepatitis ───────────────────────────────
  harvoni:          ['Hepatitis C'],
  epclusa:          ['Hepatitis C'],
  sofosbuvir:       ['Hepatitis C'],
  ribavirin:        ['Hepatitis C'],

  // ── Cancer / Chemo ──────────────────────────────────────────────
  tamoxifen:        ['Cancer'],
  letrozole:        ['Cancer'],
  anastrozole:      ['Cancer'],
  arimidex:         ['Cancer'],
  ibrance:          ['Cancer'],
  herceptin:        ['Cancer'],
  rituxan:          ['Cancer', 'Multiple Sclerosis', 'Rheumatoid', 'Lupus'],

  // ── HIV ─────────────────────────────────────────────────────────
  biktarvy:         ['HIV'],
  truvada:          ['HIV'],
  dolutegravir:     ['HIV'],
  tivicay:          ['HIV'],
  prep:             ['HIV'],

  // ── Pain / Opioids (chronic pain trigger) ───────────────────────
  oxycodone:        ['Chronic Pain'],
  oxycontin:        ['Chronic Pain'],
  hydrocodone:      ['Chronic Pain'],
  norco:            ['Chronic Pain'],
  vicodin:          ['Chronic Pain'],
  percocet:         ['Chronic Pain'],
  morphine:         ['Chronic Pain'],
  fentanyl:         ['Chronic Pain'],
  tramadol:         ['Chronic Pain'],
  ultram:           ['Chronic Pain'],
  methadone:        ['Chronic Pain'],
  suboxone:         ['Alcohol', 'Chronic Pain'],
  buprenorphine:    ['Chronic Pain'],

  // ── Sleep apnea / Oxygen ────────────────────────────────────────
  cpap:             ['Sleep Apnea'],
  bipap:            ['Sleep Apnea'],
};

// Resolve a search query to a list of FEX condition NAME FRAGMENTS
// that should match. Returns null if the query doesn't look like a
// medication match (caller falls back to direct name search).
export function medicationMatches(query) {
  const q = String(query || '').trim().toLowerCase();
  if (q.length < 3) return null;
  // Exact match wins.
  if (MEDICATION_TO_CONDITIONS[q]) return MEDICATION_TO_CONDITIONS[q];
  // Substring — picks up "metformin 500mg" → metformin, "eliquis 5mg" → eliquis.
  const hit = Object.entries(MEDICATION_TO_CONDITIONS).find(([med]) => q.includes(med));
  return hit ? hit[1] : null;
}
